import utils from '../../../Shared/utils.jsx';

/** Assign a stable $objectId on designer/property-grid model objects. */
export function ensureModelObjectId(model) {
    if (!model || typeof model !== 'object')
        return null;

    if (model.$objectId)
        return model.$objectId;

    if (model.$objectid) {
        model.$objectId = model.$objectid;
        return model.$objectId;
    }

    model.$objectId = utils.generateUUID();
    return model.$objectId;
}

function isDesignerComponentMounted(component) {
    return component && component.$ && !component.$.isUnmounted;
}

export function isNodeInDesignerScope(node, designRoot) {
    if (!node || !designRoot)
        return false;
    if (node === designRoot)
        return true;

    const path = [];
    return utils.findNodePath(path, designRoot, node);
}

function arrayIncludes(arr, node) {
    return Array.isArray(arr) && arr.includes(node);
}

function columnDefsIncludeNode(columnDefs, node) {
    if (!Array.isArray(columnDefs))
        return false;

    for (let i = 0; i < columnDefs.length; i++) {
        const col = columnDefs[i];
        if (col === node)
            return true;
        if (arrayIncludes(col?.CommandButtons, node))
            return true;
    }
    return false;
}

function findHsmState(root, node) {
    if (!root || typeof root !== 'object')
        return false;
    if (root === node)
        return true;

    const lists = [];
    if (Array.isArray(root.States))
        lists.push(root.States);
    if (Array.isArray(root.ChildStates))
        lists.push(root.ChildStates);

    for (let li = 0; li < lists.length; li++) {
        const list = lists[li];
        for (let i = 0; i < list.length; i++) {
            if (list[i] === node)
                return true;
            if (findHsmState(list[i], node))
                return true;
        }
    }
    return false;
}

function isHsmStateVisible(host, node) {
    if (!findHsmState(host.controlData, node))
        return false;

    const topstate = host.topstate;
    if (!topstate)
        return arrayIncludes(host.controlData?.States, node);

    return topstate === node || isNodeInDesignerScope(node, topstate);
}

function isWizardStepVisible(host, node) {
    const steps = host.controlData?.Steps;
    if (!arrayIncludes(steps, node))
        return false;
    const idx = steps.indexOf(node);
    return idx === (host.current_step ?? 0);
}

function isTabItemVisible(host, node) {
    const tabs = host.controlData?.TabControls;
    if (!arrayIncludes(tabs, node))
        return false;
    const idx = tabs.indexOf(node);
    return idx === (host.tab_state?.index ?? 0);
}

function resolveSubModelOnHost(host, node) {
    const cd = host.controlData;
    if (!cd)
        return null;

    if (cd.Schema === node || cd.Model === node || cd.Form === node)
        return { host, model: node };

    if (columnDefsIncludeNode(cd.ColumnDefs, node))
        return { host, model: node };

    if (arrayIncludes(cd.ContentDefs, node))
        return { host, model: node };

    if (arrayIncludes(cd.Steps, node)) {
        if (!isWizardStepVisible(host, node))
            return null;
        return { host, model: node };
    }

    if (arrayIncludes(cd.TabControls, node)) {
        if (!isTabItemVisible(host, node))
            return null;
        return { host, model: node };
    }

    if (host.controlData?.States && findHsmState(host.controlData, node)) {
        if (!isHsmStateVisible(host, node))
            return null;
        return { host, model: node };
    }

    return null;
}

export function resolveDesignerSelectionTarget(root, node) {
    if (!node?.$typeSchema || !root?.designmodel)
        return null;

    if (root.mode === 'Schema' || root.mode === 'Cassandra')
        return null;

    if (!root.viewdsgn)
        return null;

    if (!isNodeInDesignerScope(node, root.designmodel))
        return null;

    ensureModelObjectId(node);

    const refHost = root._designerByModelRef?.get(node);
    if (isDesignerComponentMounted(refHost))
        return { host: refHost, model: node };

    const registry = root._designerByModelId;
    if (node.$objectId && registry?.has(node.$objectId)) {
        const host = registry.get(node.$objectId);
        if (isDesignerComponentMounted(host))
            return { host, model: node };
    }

    if (root._designerComponents?.size) {
        for (const host of root._designerComponents) {
            if (host.designmodel === node && isDesignerComponentMounted(host))
                return { host, model: node };
        }
    }

    if (registry?.size) {
        for (const host of registry.values()) {
            if (!isDesignerComponentMounted(host))
                continue;
            const sub = resolveSubModelOnHost(host, node);
            if (sub)
                return sub;
        }
    }

    return null;
}
